import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type {
  Access_Type_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { hasPermission } from './hasPermission';
import type {
  HasPermission,
  ObjectAccess,
  ObjectWithContributors,
} from './Permission';
import { useEntityPermissions } from './useEntityPermissions';
import { useRoleAccess } from './useRoleAccess';
import { useTypePermissions } from './useTypePermissions';

/**
 * Lazy (returns-a-predicate) twin of `useHasPermissionQuery`, used where many
 * checks run after the hooks have executed (e.g. `useTabVisibility`).
 *
 * Under the `trpc` flag it mirrors `useHasPermissionQuery`: the decision is
 * resolved server-side via the perm_v2 batch endpoints — `useTypePermissions`
 * for type-level checks and `useEntityPermissions` for instance-level checks —
 * and does NOT read the legacy `role_access` catalog. Roles are now dynamic and
 * multi-valued (not derivable from a single JWT default role), so access is a
 * server question, not a client-side catalog lookup.
 *
 * Because the batch hooks must run at the top (rules of hooks) but the returned
 * predicate is invoked many times afterwards, the single entity context for
 * instance-level checks is supplied via the `entityId` hook arg. Callers that do
 * instance-level checks pass the detail entity's id (e.g.
 * `useTabVisibility` → `useHasPermissionLazy(parent?.Id)`); the predicate's
 * `parentObject` arg must refer to that same entity. Type-level checks (no
 * `parentObject`) need no `entityId`.
 *
 * The legacy (`trpc` off) path is unchanged — it resolves from `role_access`.
 */
export const useHasPermissionLazy = (entityId?: string): HasPermission => {
  const { user } = useRisksmartUser();
  const roleAccess = useRoleAccess();
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { hasTypePermission, isLoading: typeLoading } = useTypePermissions();
  const entityPerms = useEntityPermissions(entityId);

  return (
    permission: ObjectAccess | ObjectAccess[],
    parentObject?: null | ObjectWithContributors,
    canHaveAccessAsContributor?: boolean
  ) => {
    const permissionArray = Array.isArray(permission)
      ? permission
      : [permission];
    const parsed = permissionArray.map((p) => p.split(':'));

    if (parsed.some((p) => p.length !== 2)) {
      throw new Error('Invalid permission');
    }

    // V3: resolve via the server batch endpoints (mirrors useHasPermissionQuery);
    // the role_access catalog is not consulted.
    if (trpcEnabled) {
      // If a parentObject is supplied, this lazy hook must be bound to that same
      // entity id; falling back to type-level checks would be unsafe.
      if (parentObject && parentObject.Id !== entityId) {
        return { hasPermission: false, loading: false };
      }

      // Instance-level only when a parentObject is provided (bound via entityId).
      const instanceLevel = !!parentObject;
      const allowed = parsed.some(([accessType, objectType]) => {
        if (!accessType || !objectType) {
          return false;
        }

        if (instanceLevel) {
          switch (accessType) {
            case 'read':
              return entityPerms.canRead;
            case 'update':
              return entityPerms.canUpdate;
            case 'delete':
              return entityPerms.canDelete;
            case 'insert':
            case 'create':
              return entityPerms.canInsertChild;
            default:
              return false;
          }
        }

        return hasTypePermission(accessType, objectType);
      });

      return {
        hasPermission: allowed,
        loading: instanceLevel ? entityPerms.isLoading : typeLoading,
      };
    }

    // Legacy (trpc off): resolve from the role_access catalog.
    if (!roleAccess) {
      return {
        hasPermission: false,
        loading: false,
      };
    }

    return {
      hasPermission: parsed.some(([accessType, objectType]) => {
        return hasPermission({
          parentObject,
          userId: user?.userId,
          roleAccess,
          objectType: objectType as Parent_Type_Enum,
          accessType: accessType as Access_Type_Enum,
          canHaveAccessAsContributor,
        });
      }),
      loading: false,
    };
  };
};
